// @flow

import React, { type Node } from 'react';
import { type LookAndFeelRoot } from '@tectonics/look-and-feel';
import { type PaymentSpec } from '@tectonics/prs/payment-spec';
import { type IncomingPayment } from '@tectonics/prs/incoming-payment';
import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { type UserInputName } from '@tectonics/prs/user-input';
import { type UserInputValidationErrors } from '@tectonics/prs/validation';
import { type PaymentMethod, type PaymentMethodCode } from '@tectonics/prs/payment-method';
import { type AnalyticsManager } from '@tectonics/analytics';
import { type AppValues } from '@tectonics/app-values';
import { type PlaceOrderErrorPayload } from '@tectonics/prs/outgoing-payment';

type LookAndFeelRenderProps = {|
  root: LookAndFeelRoot,
  spec: PaymentSpec,
  incomingPayment: IncomingPayment,
  appValues: AppValues,
  setAppValues: (AppValues) => void,
  userInputValues: UserInputValues,
  onUserInput: (name: UserInputName, value: any) => void,
  userInputValidationErrors: UserInputValidationErrors,
  selectedPaymentMethod: ?PaymentMethod,
  setSelectedPaymentMethodByCode: (PaymentMethodCode) => void,
  submitting: boolean,
  handleSubmit: (boolean, boolean) => Promise<bool>,
  submitError: ?PlaceOrderErrorPayload,
  +analytics: AnalyticsManager,
  +requestReloadContext: () => void,
|};

export function LookAndFeelRender({
  root: LafRoot,
  spec,
  incomingPayment,
  appValues,
  setAppValues,
  userInputValues,
  onUserInput,
  userInputValidationErrors,
  selectedPaymentMethod,
  setSelectedPaymentMethodByCode,
  submitting,
  handleSubmit,
  submitError,
  analytics,
  requestReloadContext,
}: LookAndFeelRenderProps): Node {
  switch (incomingPayment.status) {
    case 'OP':
      return (
        <LafRoot
          stage="payment-opened"
          spec={spec}
          incomingPayment={incomingPayment}
          appValues={appValues}
          setAppValues={setAppValues}
          onUserInput={onUserInput}
          userInputValues={userInputValues}
          userInputValidationErrors={userInputValidationErrors}
          selectedPaymentMethod={selectedPaymentMethod}
          onSelectPaymentMethod={setSelectedPaymentMethodByCode}
          submitting={submitting}
          onSubmit={handleSubmit}
          submitError={submitError}
          analytics={analytics}
          requestReloadContext={requestReloadContext}
        />
      );

    case 'CA':
      return (
        <LafRoot
          stage="payment-canceled"
          spec={spec}
          incomingPayment={incomingPayment}
          userInputValues={userInputValues}
          onUserInput={onUserInput}
          analytics={analytics}
        />
      );

    case 'CO':
      return (
        <LafRoot
          stage="payment-confirmed"
          spec={spec}
          incomingPayment={incomingPayment}
          userInputValues={userInputValues}
          onUserInput={onUserInput}
          analytics={analytics}
        />
      );

    case 'PE':
      return (
        <LafRoot
          stage="payment-pending"
          spec={spec}
          incomingPayment={incomingPayment}
          userInputValues={userInputValues}
          onUserInput={onUserInput}
          analytics={analytics}
          requestReloadContext={requestReloadContext}
        />
      );

    default: throw new Error(`Controller: incoming payment status not supported: "${incomingPayment.status}"`);
  }
}
